import { resolveDateOnly, resolveDateTime } from '../relativeDates';
import { escXml } from './esc';

export interface Pain001Input {
  amount: string;
  currency: string;
  endToEndId: string;
  instructionId: string;
  msgId: string;
  pmtInfId: string;
  debtorName: string;
  debtorIban: string;
  debtorBic: string;
  creditorName: string;
  creditorIban: string;
  creditorBic: string;
  remittance: string;
  executionDate: string;
  createdAt: string;
  xmlns: string;
  /** Document xmlns for the paired pain.002 */
  pairXmlns: string;
  nbOfTxs: string;
  ctrlSum: string;
  initgPtyName: string;
  pmtMtd: string;
  svcLvl: string;
  chrgBr: string;
  statusMsgId: string;
  orgnlMsgNmId: string;
  addtlInf: string;
}

const now = new Date();
const today = now.toISOString().slice(0, 10);
const created = now.toISOString().replace(/\.\d{3}Z$/, '');

export const DEFAULT_PAIN001_INPUT: Pain001Input = {
  amount: '1250.00',
  currency: 'EUR',
  endToEndId: 'E2E-TRY-2026-0001',
  instructionId: 'INSTR-TRY-0001',
  msgId: 'PAIN1-TRY-0001',
  pmtInfId: 'PMT-TRY-0001',
  debtorName: 'Marie Lefebvre',
  debtorIban: 'FR7630006000011234567890189',
  debtorBic: 'DEMOFRPPXXX',
  creditorName: 'Atelier Rousseau SARL',
  creditorIban: 'DE89370400440532013000',
  creditorBic: 'COBADEFFXXX',
  remittance: 'Try editor payment',
  executionDate: today,
  createdAt: created,
  xmlns: 'urn:iso:std:iso:20022:tech:xsd:pain.001.001.09',
  pairXmlns: 'urn:iso:std:iso:20022:tech:xsd:pain.002.001.10',
  nbOfTxs: '1',
  ctrlSum: '1250.00',
  initgPtyName: 'Aggregateur SAS',
  pmtMtd: 'TRF',
  svcLvl: 'SEPA',
  chrgBr: 'SLEV',
  statusMsgId: 'PAIN2-TRY-0001',
  orgnlMsgNmId: 'pain.001.001.09',
  addtlInf: 'Synthetic reject from the Try Editor',
};

export function freshPain001Input(): Pain001Input {
  const executionDate = resolveDateOnly('today');
  const createdAt = resolveDateTime('now');
  return { ...DEFAULT_PAIN001_INPUT, executionDate, createdAt };
}

export function buildPain001(input: Pain001Input): string {
  const svcLvl = input.svcLvl.trim()
    ? `
      <PmtTpInf>
        <SvcLvl><Cd>${escXml(input.svcLvl)}</Cd></SvcLvl>
      </PmtTpInf>`
    : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="${escXml(input.xmlns)}">
  <CstmrCdtTrfInitn>
    <GrpHdr>
      <MsgId>${escXml(input.msgId)}</MsgId>
      <CreDtTm>${escXml(input.createdAt)}</CreDtTm>
      <NbOfTxs>${escXml(input.nbOfTxs)}</NbOfTxs>
      <CtrlSum>${escXml(input.ctrlSum)}</CtrlSum>
      <InitgPty><Nm>${escXml(input.initgPtyName)}</Nm></InitgPty>
    </GrpHdr>
    <PmtInf>
      <PmtInfId>${escXml(input.pmtInfId)}</PmtInfId>
      <PmtMtd>${escXml(input.pmtMtd)}</PmtMtd>
      <BtchBookg>false</BtchBookg>
      <NbOfTxs>${escXml(input.nbOfTxs)}</NbOfTxs>
      <CtrlSum>${escXml(input.ctrlSum)}</CtrlSum>${svcLvl}
      <ReqdExctnDt><Dt>${escXml(input.executionDate)}</Dt></ReqdExctnDt>
      <Dbtr><Nm>${escXml(input.debtorName)}</Nm></Dbtr>
      <DbtrAcct><Id><IBAN>${escXml(input.debtorIban)}</IBAN></Id><Ccy>${escXml(input.currency)}</Ccy></DbtrAcct>
      <DbtrAgt><FinInstnId><BICFI>${escXml(input.debtorBic)}</BICFI></FinInstnId></DbtrAgt>
      <ChrgBr>${escXml(input.chrgBr)}</ChrgBr>
      <CdtTrfTxInf>
        <PmtId>
          <InstrId>${escXml(input.instructionId)}</InstrId>
          <EndToEndId>${escXml(input.endToEndId)}</EndToEndId>
        </PmtId>
        <Amt><InstdAmt Ccy="${escXml(input.currency)}">${escXml(input.amount)}</InstdAmt></Amt>
        <CdtrAgt><FinInstnId><BICFI>${escXml(input.creditorBic)}</BICFI></FinInstnId></CdtrAgt>
        <Cdtr><Nm>${escXml(input.creditorName)}</Nm></Cdtr>
        <CdtrAcct><Id><IBAN>${escXml(input.creditorIban)}</IBAN></Id></CdtrAcct>
        <RmtInf><Ustrd>${escXml(input.remittance)}</Ustrd></RmtInf>
      </CdtTrfTxInf>
    </PmtInf>
  </CstmrCdtTrfInitn>
</Document>`;
}

export function buildPain002(input: Pain001Input, outcome: string, reasonCode = 'AC01'): string {
  const statusMsgId = input.statusMsgId.trim() || input.msgId.replace(/PAIN1/i, 'PAIN2') || 'PAIN2-TRY-0001';
  const rejectBlock =
    outcome === 'RJCT'
      ? `<StsRsnInf><Rsn><Cd>${escXml(reasonCode)}</Cd></Rsn><AddtlInf>${escXml(input.addtlInf)}</AddtlInf></StsRsnInf>`
      : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="${escXml(input.pairXmlns)}">
  <CstmrPmtStsRpt>
    <GrpHdr>
      <MsgId>${escXml(statusMsgId)}</MsgId>
      <CreDtTm>${escXml(input.createdAt)}</CreDtTm>
    </GrpHdr>
    <OrgnlGrpInfAndSts>
      <OrgnlMsgId>${escXml(input.msgId)}</OrgnlMsgId>
      <OrgnlMsgNmId>${escXml(input.orgnlMsgNmId)}</OrgnlMsgNmId>
      <GrpSts>${escXml(outcome)}</GrpSts>
      ${rejectBlock}
    </OrgnlGrpInfAndSts>
    <OrgnlPmtInfAndSts>
      <OrgnlPmtInfId>${escXml(input.pmtInfId)}</OrgnlPmtInfId>
      <PmtInfSts>${escXml(outcome)}</PmtInfSts>
      <TxInfAndSts>
        <OrgnlInstrId>${escXml(input.instructionId)}</OrgnlInstrId>
        <OrgnlEndToEndId>${escXml(input.endToEndId)}</OrgnlEndToEndId>
        <TxSts>${escXml(outcome)}</TxSts>
        ${outcome === 'RJCT' ? `<StsRsnInf><Rsn><Cd>${escXml(reasonCode)}</Cd></Rsn></StsRsnInf>` : ''}
      </TxInfAndSts>
    </OrgnlPmtInfAndSts>
  </CstmrPmtStsRpt>
</Document>`;
}
